import React, { Component, Fragment } from 'react';
import PageTitle from "../../../PageTitle";
import { ICDBPageProps } from "../../../../utils/ICDBPageProps";
import { CommandBarButton, Dropdown, IDropdownOption, TextField } from "office-ui-fabric-react";
import {
    AllocateTenantRolloverData,
    AllocateTenantRolloverDataProvider
} from "../../../rollover/AllocateTenantRolloverDataProvider";
import Table, { TableColumn, TableItem } from "../../../Table";
import BoolPill from "../../../BoolPill";
import TableCell from "../../../TableCell";
import * as _ from "lodash";
import { RolloverSummary } from "../../../../models/rollover/RolloverSummary";
import LoadingPage from "../../../LoadingPage";
import TimeTrafficCellContent from "../../../TimeTrafficCellContent";
import { TableRowProps } from "../../../TableRow";
import { Link } from "react-router-dom";
import * as H from "history";
import { handleDropdownMultiSelect } from "../../../SchoolFilterControls";

type RolloverType = "mailroom" | "thirdPartySDS";

interface RolloverSummariesPageState {
    result?: RolloverSummary[];

    filterString?: string;
    filteredResult?: RolloverSummary[];
}

interface SummaryQuery {
    types: RolloverType[];
    agentIds: string[];
    isRolledOver: boolean | null;
}

const columns: TableColumn[] = [
    { title: "Tenant", propertyPath: "tenantName", enableSort: true, flex: 1 },
    { title: "School", propertyPath: "rollover.schoolName", enableSort: true, flex: 2 },
    { title: "Type", propertyPath: "type", flex: 1, enableSort: true },
    { title: "Agent", propertyPath: "tenantAllocatedToId", extraDetail: "Mailroom only", flex: 1, enableSort: true },
    { title: "Rollover Date", propertyPath: "form.rolloverDate", extraDetail: "Mailroom only", type: "date", enableSort: true, flex: 1 },
    { title: "Student Memberships Date", propertyPath: "form.addStudentMembershipsDate", extraDetail: "Mailroom only", type: "date", enableSort: true, flex: 1 },
    { title: "Customer Confirmed", propertyPath: "form.customerConfirmed", extraDetail: "Mailroom only", flex: 1, enableSort: true },
    { title: "Notes", propertyPath: "form.adminNotes", extraDetail: "Mailroom only", flex: 1, enableSort: true },
    { title: "Status", propertyPath: "rollover.isRolledOver", flex: 1, enableSort: true },
];

export default class RolloverSummariesPage extends Component<ICDBPageProps, RolloverSummariesPageState> {
    static displayName = RolloverSummariesPage.name;

    static schoolLink = (tenantId: string) : string => `/cdb-admin/rollover/tenant/${tenantId}`;

    constructor(props: ICDBPageProps) {
        super(props);

        this.state = {
            result: undefined
        }
    }

    readQuery(location: H.Location): SummaryQuery {
        const searchParams = new URLSearchParams(location.search);

        return {
            agentIds: searchParams.getAll("agentIds") || [],
            types: (searchParams.getAll("types") || []) as RolloverType[],
            isRolledOver: searchParams.get("isRolledOver") as any
        }
    }

    updateQuery(newQuery: SummaryQuery) {

        const searchParams = new URLSearchParams();

        newQuery.types?.forEach(x => searchParams.append("types", x));
        newQuery.agentIds?.forEach(x => searchParams.append("agentIds", x));
        if (newQuery.isRolledOver !== null) {
            searchParams.append("isRolledOver", newQuery.isRolledOver as any);
        }

        this.props.history.push({ search: searchParams.toString() })
    }

    async componentDidMount() {
        await this.loadSummaries();
    }

    async loadSummaries() {
        const query = this.readQuery(this.props.location);
        const result = await this.props.apiService.getRolloversSummaries(query.types, query.agentIds, query.isRolledOver);

        this.setState({
            result: result
        });
    }

    filterResults() {
        if (this.state.result === undefined) {
            this.setState({ filteredResult: undefined });
            return;
        }

        if (this.state.filterString === undefined) {
            this.setState({ filteredResult: this.state.result });
            return;
        }

        const filteredResult = this.state.result?.filter(summary => {
            if (summary.tenantName.toLowerCase().indexOf(this.state.filterString!) !== -1) {
                return true;
            }

            if (summary.rollover.schoolCode.toLowerCase().indexOf(this.state.filterString!) !== -1) {
                return true;
            }

            if (summary.rollover.schoolName.toLowerCase().indexOf(this.state.filterString!) !== -1) {
                return true;
            }

            return false;
        });

        this.setState({ filteredResult });
    }

    async componentDidUpdate(prevProps: Readonly<ICDBPageProps>, prevState: RolloverSummariesPageState): Promise<void> {
        if (this.props.location !== prevProps.location) {
            await this.loadSummaries();
        }

        if (this.state.filterString !== prevState.filterString || this.state.result !== prevState.result) {
            this.filterResults();
        }
    }

    renderFilters(allocateTenantData: AllocateTenantRolloverData): JSX.Element {
        const typeOptions: IDropdownOption[] = [
            {
                key: "mailroom",
                text: "Mailroom"
            },
            {
                key: "thirdPartySDS",
                text: "Third Party SDS"
            }
        ];

        const isRolledOverOptions: IDropdownOption[] = [
            {
                key: "null",
                data: null,
                text: "All"
            },
            {
                key: "true",
                data: true,
                text: "Rolled Over"
            },
            {
                key: "false",
                data: false,
                text: "Not Rolled Over"
            }
        ];

        const agentOptions: IDropdownOption[] = allocateTenantData.availableAgentIds.map(agentId => {
            return {
                key: agentId,
                text: allocateTenantData.agentUserDict[agentId].displayName
            };
        })

        agentOptions.push({
            key: "notAllocated",
            text: "Not allocated"
        });

        const query = this.readQuery(this.props.location);

        return (
            <div className="filter-controls p-top-1rem">
                <div className="filter-controls__row">
                    <TextField className="filter-controls__input" autoFocus label="Filter by Name" placeholder="Filter"
                        onKeyUp={(keyEvent) => {
                            if (keyEvent.key === "Enter") {
                                if (this.state.filteredResult?.length === 1) {
                                    const summary = this.state.filteredResult[0];
                                    this.props.history.push(RolloverSummariesPage.schoolLink(summary.tenantId));
                                }
                            }
                        }}
                        onChange={
                            (event, newValue) => {
                                this.setState({
                                    filterString: newValue !== undefined ? newValue.toLowerCase() : ""
                                });
                            }
                        } />

                    <Dropdown
                        className={"filter-controls__input"}
                        styles={{ dropdown: { width: 300 } }}
                        label={"Type"}
                        options={typeOptions}
                        multiSelect={true}
                        selectedKeys={query.types}
                        onChange={(e, option) => {
                            handleDropdownMultiSelect(option, () => query.types, (newKeys) => {
                                query.types = newKeys;
                                this.updateQuery(query);
                            })
                        }} />

                    <Dropdown
                        className={"filter-controls__input"}
                        styles={{ dropdown: { width: 300 } }}
                        label={"Agents"}
                        options={agentOptions}
                        multiSelect={true}
                        selectedKeys={query.agentIds}
                        onChange={(e, option) => {
                            handleDropdownMultiSelect(option, () => query.agentIds, (newKeys) => {
                                query.agentIds = newKeys;
                                this.updateQuery(query)
                            }
                            )
                        }} />
                    <Dropdown
                        className={"filter-controls__input"}
                        styles={{ dropdown: { width: 300 } }}
                        label={"Status"}
                        options={isRolledOverOptions}
                        selectedKey={query.isRolledOver == null ? "null" : query.isRolledOver.toString()}
                        onChange={(e, o) => {
                            query.isRolledOver = o?.data!;
                            this.updateQuery(query)
                        }}
                    />
                </div>

            </div>
        );
    }


    private cellContentForItem(item: RolloverSummary, propertyPath: string, allocateData: AllocateTenantRolloverData): JSX.Element | undefined | null {
        const value = _.get(item, propertyPath);

        if (propertyPath === "rollover.isRolledOver") {
            return (<BoolPill trueText="Rolled Over" falseText="Not Rolled Over" value={item.rollover.isRolledOver} falseColour="grey" />);
        }

        if (propertyPath === "rollover.schoolName") {
            return <Fragment>
                <span>{item.rollover.schoolName}</span>
                <span className="cdb-table__extra-detail">{item.rollover.schoolCode}</span>
            </Fragment>
        }

        if (propertyPath.startsWith("form.")) {
            //All form cells blank if no form
            if (item.form === null) {
                return null;
            }

            switch (propertyPath) {
                case "form.customerConfirmed":
                    {
                        return (<BoolPill trueText="Customer Confirmed" falseText="Unconfirmed" value={item.form?.customerConfirmed} falseColour="grey" />);
                    }
                case "form.rolloverDate":
                    {
                        return (<TimeTrafficCellContent date={item.form.rolloverDate} enableColour={!item.rollover.isRolledOver} amberOffset={0} redOffset={1} />);
                    }
                case "form.addStudentMembershipsDate":
                    {
                        return (<TimeTrafficCellContent date={item.form.addStudentMembershipsDate} enableColour={item.rollover.areStudentsEnabled === false} amberOffset={0} />);
                    }
                default:
                    return undefined;
            }
        }

        if (propertyPath === "tenantAllocatedToId") {
            if (item.rollover.thirdPartySDS) {
                return null;
            }
            if (value === null) {
                return (<div className={`pill pill--amber`}>Not allocated</div>)
            }
            return <span>{allocateData.agentUserDict[value].displayName}</span>
        }

        return undefined;
    }

    private cellForColumn = (item: RolloverSummary, column: TableColumn, columnIndex: number, allocateData: AllocateTenantRolloverData): JSX.Element | undefined => {
        let cellContent = this.cellContentForItem(item, column.propertyPath, allocateData);
        if (cellContent === undefined) {
            return undefined;
        }
        return (
            <TableCell key={columnIndex} flex={column.flex}>
                {cellContent}
            </TableCell>
        )
    }

    private rowPropsForItem = (item: TableItem): TableRowProps => {

        const summary = item as RolloverSummary;
        const enableRowLink = summary.type === "mailroom";

        return {
            enableRowLink: enableRowLink,
            to: enableRowLink ? RolloverSummariesPage.schoolLink(summary.tenantId) : undefined
        }
    }

    renderPage(allocateTenantData: AllocateTenantRolloverData): JSX.Element {

        if (this.state.filteredResult === undefined) {
            return (<LoadingPage />);
        }

        return (
            <Fragment>
                {this.renderFilters(allocateTenantData)}
                <Table
                    columns={columns}
                    items={this.state.filteredResult}
                    enableSort={true}
                    cellForColumn={(item, column, columnIndex) => this.cellForColumn(item as RolloverSummary, column, columnIndex, allocateTenantData)}
                    rowPropsForItem={this.rowPropsForItem}
                >
                    <Link to={`/cdb-admin/rollover/tenants`}>
                        <CommandBarButton
                            text="Tenants"
                            iconProps={{ iconName: "list" }}
                            onClick={() => { }}
                        />
                    </Link>

                    <Link to={`/cdb-admin/rollover/statistics`}>
                        <CommandBarButton
                            iconProps={{ iconName: "lineChart" }}
                            text="Statistics (Legacy)"
                            onClick={() => { }}
                        />
                    </Link>
                </Table>
            </Fragment>
        )
    }

    render() {
        return (
            <Fragment>
                <PageTitle title={"Rollovers"} />

                <AllocateTenantRolloverDataProvider apiService={this.props.apiService} graphClient={this.props.graphClient}>
                    {(data: AllocateTenantRolloverData) => {
                        return this.renderPage(data);
                    }}
                </AllocateTenantRolloverDataProvider>
            </Fragment>
        );
    }
}